import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  VStack,
  Text,
  Center,
  HStack,
  Input,
  Button,
  Alert,
  AlertIcon,
  Badge,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Select,
  Divider,
} from "@chakra-ui/react";
import { useAppContext } from "../../../context/AppContext";
import {
  CreateUserDropBoxConfig,
  fetchUserDropBoxConfig,
  RefreshAccessToken,
  CreateUserListDropBoxBusinessConfig,
} from "../../../services/DropBox/dropBoxservice";
import {
  DropBoxUserConfigModel,
  DropBoxTeamInfo,
  DropboxUserInfo,
  TeamMember,
  TeamMembersResponse,
  TeamMemberNew,
  TeamMembersApiResponse,
} from "../../../models/DropBoxUserConfigModel";

const DropboxBusinessAdminConfig = () => {
  const {
    accessToken,
    dropBoxAccessToken,
    setDropBoxAccessToken,
    dropBoxRefreshToken,
    setDropBoxRefreshToken,
    userData,
    dropBoxAppKey,
    setDropBoxAppKey,
    dropBoxAppSecret,
    setDropBoxAppSecret,
  } = useAppContext();

  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [teamInfo, setTeamInfo] = useState<DropBoxTeamInfo | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMemberNew[]>([]);
  const [error, setError] = useState("");
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [savingMembers, setSavingMembers] = useState(false);
  const [dropBoxIntegrationId, setDropBoxIntegrationId] = useState<
    number | null
  >(null);
  const toast = useToast();

  useEffect(() => {
    const initializeDropboxConfig = async () => {
      try {
        setLoading(true);

        const config = await fetchUserDropBoxConfig(
          accessToken || "",
          userData?.office365MailId || "",
          userData?.userID || 0,
          userData?.tenantID || 0
        );

        if (!config) {
          toast({
            title: "No Dropbox Configuration Found",
            status: "info",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
          setIsAuthenticated(false);
          return;
        }

        // Set context values
        setDropBoxAppKey(config.clinetID);
        setDropBoxAppSecret(config.clinetSecret);
        setDropBoxRefreshToken(config.refreshToken);
        // Store the integration ID for saving team members
        setDropBoxIntegrationId(config.dropBoxIntegrationId || null); // Assuming the config has an ID field

        const isValid =
          config.clinetID && config.clinetSecret && config.refreshToken;

        if (!isValid) {
          toast({
            title: "Incomplete Configuration",
            description: "Missing App Key, Secret, or Refresh Token.",
            status: "warning",
            duration: 4000,
            isClosable: true,
            position: "top",
          });
          setIsAuthenticated(false);
          return;
        }

        // Get access token from refresh token
        const newDropBoxAccessToken = await RefreshAccessToken(
          config.clinetID,
          config.clinetSecret,
          config.refreshToken
        );

        if (newDropBoxAccessToken) {
          setDropBoxAccessToken(newDropBoxAccessToken);
          setIsAuthenticated(true);

          toast({
            title: "Dropbox Connected",
            description: "Access token successfully refreshed.",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });

          // Call your custom logic after authentication
          await fetchTeamMembers();
        } else {
          setIsAuthenticated(false);
          toast({
            title: "Token Refresh Failed",
            description: "Could not get access token from refresh token.",
            status: "error",
            duration: 4000,
            isClosable: true,
            position: "top",
          });
        }
      } catch (err) {
        // console.error("Initialization error:", err);
        toast({
          title: "Initialization Error",
          description: "An error occurred while setting up Dropbox config.",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initializeDropboxConfig();
  }, []);

  useEffect(() => {
    if (
      (dropBoxAppKey ?? "") &&
      (dropBoxAppSecret ?? "") &&
      (dropBoxAccessToken ?? "") &&
      (dropBoxRefreshToken ?? "")
    ) {
      console.log("Dropbox Business Config found");
      setIsAuthenticated(true);
      fetchTeamMembers();
      if (loading) {
        setIsAuthenticated(false);
      }
    } else {
      setIsAuthenticated(false);
      console.log("Dropbox Business Config NOT found");
    }
  }, [
    dropBoxAppKey,
    dropBoxAppSecret,
    dropBoxAccessToken,
    dropBoxRefreshToken,
  ]);

  // Updated fetch function
  const fetchTeamMembers = async (): Promise<TeamMemberNew[]> => {
    if (!dropBoxAccessToken) return [];

    try {
      setLoadingMembers(true);
      let allMembers: TeamMemberNew[] = [];
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/DropBoxBusiness/TeamMemberList`,
          {
            method: "POST",
            headers: {
              Authorization: `${dropBoxAccessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          // Parse the response as the new API structure
          const apiResponse: TeamMembersApiResponse = await response.json();
          console.log("API Response OF DB:", apiResponse);

          // Check if the API call was successful
          if (apiResponse.isSuccess) {
            // Extract members from the response
            allMembers = [...allMembers, ...apiResponse.members];
            console.log("Data of team members is:", apiResponse.members);

            // Handle pagination if needed (based on totalCount vs current count)
            // Since your current response doesn't seem to include cursor/pagination info,
            // we'll assume no pagination for now
            hasMore = false;

            // Optional: You can use totalCount for validation
            console.log(`Total count from API: ${apiResponse.totalCount}`);
            console.log(
              `Actual members received: ${apiResponse.members.length}`
            );
          } else {
            // Handle API error
            const errorMsg =
              apiResponse.errorMessage || "Unknown error occurred";
            // console.error("API returned error:", errorMsg);
            throw new Error(`API Error: ${errorMsg}`);
          }
        } else {
          const errorText = await response.text();
          console.error(
            "HTTP Error - Failed to fetch team members:",
            errorText
          );
          throw new Error(`HTTP Error: ${response.status} - ${errorText}`);
        }
      }

      setTeamMembers(allMembers);
      console.log(`Successfully loaded ${allMembers.length} team members`);
      console.log("Team Member details:", allMembers);
      return allMembers;
    } catch (error) {
      console.error("Error fetching team members:", error);
      // setError(`Failed to fetch team members: ${(error as Error).message}`);
      return [];
    } finally {
      setLoadingMembers(false);
    }
  };

  // Replace your saveTeamMembersToBackend function with this updated version:
  const saveTeamMembersToBackend = async () => {
    
    if (!teamMembers || teamMembers.length === 0) {
      setError("No team members to save");
      return;
    }

    try {
      setSavingMembers(true);
      setError("");

      // Map team members to the backend model
      const dropBoxUserList = teamMembers.map((member) => ({
        DropBoxIntegrationId: dropBoxIntegrationId,
        DropBoxTeamMemberId: member.teamMemberId,
        DropBoxAccountId: member.accountId,
        DropBoxUserEmail: member.email,
        DropBoxDisplayName: member.displayName,
        DropBoxMembershipType: member.membershipType,
        DropBoxStatus: member.membershipStatus,
        DropBoxIsEmailVerified: member.isVerifiedEmail,
      }));

      // Use the service function
      const result = await CreateUserListDropBoxBusinessConfig(
        dropBoxUserList,
        accessToken || "",
        userData?.tenantID || 0
      );

      if (result.success) {
        toast({
          title: "Team Members Saved",
          description: `Successfully saved ${teamMembers.length} team members to database.`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
        console.log("Team members saved successfully:", result);
      } else {
        throw new Error(result.message || "Failed to save team members");
      }
    } catch (error) {
      // console.error("Error saving team members:", error);
      // setError(`Failed to save team members: ${(error as Error).message}`);
      toast({
        title: "Save Failed",
        description: `Failed to save team members: ${(error as Error).message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setSavingMembers(false);
    }
  };

  // Verify app configuration before authentication "Check this is valid or not"
  const verifyAppConfig = async () => {
    if (!(dropBoxAppKey ?? "")) {
      setError("App Key is required");
      return false;
    }

    // Basic App Key format validation
    if (dropBoxAppKey.length < 10 || !/^[a-zA-Z0-9]+$/.test(dropBoxAppKey)) {
      setError(
        "App Key format appears invalid. Please check your App Key from Dropbox App Console."
      );
      return false;
    }

    return true;
  };

  // Handle OAuth authentication for Business admin access - First call on the typed key and secrets
  const handleDropboxBusinessAuth = async () => {
    if (!(await verifyAppConfig())) {
      return;
    }

    if (!(dropBoxAppSecret ?? "")) {
      setError("App Secret is required");
      return;
    }

    setLoading(true);
    setIsAuthenticated(false);
    setError("");
    console.log("Starting Business admin authentication");
    console.log("App Key being used:", dropBoxAppKey);

    try {
      const teamScopes = [
        "account_info.read",
        "files.metadata.read",
        "files.content.read",
        "files.content.write",
        "files.metadata.write",
        "sharing.read",
        "team_data.governance.read",
        "team_data.member",
        "team_data.team_space",
        "team_info.read",
        "members.read",
        "members.write",
      ].join(" ");

      const authUrl = `https://www.dropbox.com/oauth2/authorize?response_type=code&client_id=${encodeURIComponent(
        dropBoxAppKey
      )}&token_access_type=offline&scope=${encodeURIComponent(teamScopes)}`;

      console.log("Auth URL:", authUrl);

      window.open(
        authUrl,
        "dropboxBusinessAuth",
        "width=600,height=700,scrollbars=yes,resizable=yes"
      );

      // Immediately show the manual code input
      setTimeout(() => {
        setShowManualInput(true);
        setLoading(false);
      }, 1000); // Or show instantly
    } catch (error) {
      // console.error("Business auth window error:", error);
      setError(`Authentication error`);
      setLoading(false);
    }
  };

  // Exchange code for Business API tokens => this will call two time one is for the autoamtion and other time for the manual code exchage
  const exchangeCodeForBusinessToken = async (code: string) => {
    if (!(dropBoxAppKey ?? "") || !(dropBoxAppSecret ?? "") || !(code ?? "")) {
      setError("Missing required parameters for token exchange");
      return;
    }

    try {
      const params = new URLSearchParams({
        code: code,
        grant_type: "authorization_code",
        client_id: dropBoxAppKey,
        client_secret: dropBoxAppSecret,
      });

      const response = await fetch("https://api.dropboxapi.com/oauth2/token", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const data = await response.json();
      console.log("Business token data:", data);

      if (response.ok && data.access_token && data.refresh_token) {
        setDropBoxAccessToken(data.access_token);
        setDropBoxRefreshToken(data.refresh_token);
        console.log("Access Token after code exchange", data.access_token);
        console.log("Refresh Token after code exchange", data.refresh_token);

        // Try to verify team access
        const teamInfoResponse = await fetch(
          "https://api.dropboxapi.com/2/team/get_info",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${data.access_token}`,
              "Content-Type": "application/json",
            },
          }
        );

        let configType = "business";
        let teamId = null;

        if (teamInfoResponse.ok) {
          const teamData = await teamInfoResponse.json();
          setTeamInfo(teamData);
          teamId = teamData.team_id || null;
          console.log("Team access verified");

          // Fetch team members immediately
          await fetchTeamMembers();
        } else {
          console.log(
            "Team info failed, but continuing with team-level access"
          );
          // Even if team info fails, we can still have team-level access
          // Try to fetch team members
          await fetchTeamMembers();
        }

        setIsAuthenticated(true);

        // Save configuration
        const dropBoxConfig: DropBoxUserConfigModel = {
          userId: userData?.userID,
          tenantId: userData?.tenantID,
          userEmail: userData?.office365MailId,
          clinetID: dropBoxAppKey,
          clinetSecret: dropBoxAppSecret,
          refreshToken: data.refresh_token,
          configType: configType,
          teamId: teamId,
          isBusiness: true,
        };

        const configResponse = await CreateUserDropBoxConfig(
          dropBoxConfig,
          accessToken,
          userData?.tenantID
        );

        if (!configResponse.success) {
          console.error("Failed to save Dropbox configuration");
          setError("Failed to save configuration");
          setIsAuthenticated(false);
        } else {
          console.log("Authentication successful");
        }
      } else {
        setError(`Token exchange failed: ${data.error || "Unknown error"}`);
        if (data.error === "invalid_client") {
          setError(
            "Invalid App Key or App Secret. Please verify your app credentials."
          );
        }
      }
    } catch (error) {
      console.error("Token exchange error:", error);
      // setError(`Token exchange error: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle manual code submission for Business auth
  const handleManualCodeSubmit = async () => {
    if (!manualCode) {
      setError("Authorization code is required");
      return;
    }

    setLoading(true);
    setError("");
    await exchangeCodeForBusinessToken(manualCode);
    setShowManualInput(false);
    setManualCode("");
  };

  // Test connection by fetching team members
  const testAdminConnection = async () => {
    if (!dropBoxAccessToken) return;

    setLoading(true);
    try {
      const members = await fetchTeamMembers();

      if (members.length > 0) {
        toast({
          title: "Team access verified!",
          description: `Found ${members.length} team members.`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
        setError("");
      } else {
        setError("No team members found or insufficient permissions");
        toast({
          title: "Connection test failed",
          description: "No team members found or insufficient permissions",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (error) {
      // setError(`Connection test failed: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      maxW="800px"
      mx="auto"
      mt="40px"
      p="6"
      borderRadius="md"
      boxShadow="lg"
      bg="white"
    >
      <VStack spacing="4" align="stretch">
        <Center pb="4">
          <HStack spacing="2">
            <VStack spacing="1" align="center">
              <Heading size="md">Dropbox Business Admin</Heading>
              <Badge
                colorScheme={
                  teamInfo ? "blue" : teamMembers.length > 0 ? "green" : "gray"
                }
                size="sm"
              >
                {teamInfo
                  ? "Admin Level Access"
                  : teamMembers.length > 0
                  ? "Team Access"
                  : "Not Connected"}
              </Badge>
            </VStack>
          </HStack>
        </Center>

        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Box>
          <Text fontWeight="bold" mb="2">
            App Key
          </Text>
          <Input
            placeholder="Enter App Key (e.g., abc123def456)"
            value={dropBoxAppKey}
            onChange={(e) => {
              setDropBoxAppKey(e.target.value.trim());
              setError(""); // Clear errors when user types
            }}
          />
          <Text fontSize="xs" color="gray.500" mt="1">
            Get this from your Dropbox app Settings → App Key
          </Text>
          {dropBoxAppKey && (
            <Text
              fontSize="xs"
              color={
                dropBoxAppKey.length > 10 &&
                /^[a-zA-Z0-9]+$/.test(dropBoxAppKey)
                  ? "green.500"
                  : "red.500"
              }
              mt="1"
            >
              Format:{" "}
              {dropBoxAppKey.length > 10 && /^[a-zA-Z0-9]+$/.test(dropBoxAppKey)
                ? "✓ Valid"
                : "✗ Invalid format"}
            </Text>
          )}
        </Box>

        <Box>
          <Text fontWeight="bold" mb="2">
            App Secret
          </Text>
          <Input
            type="password"
            placeholder="Enter App Secret"
            value={dropBoxAppSecret}
            onChange={(e) => setDropBoxAppSecret(e.target.value)}
          />
          <Text fontSize="xs" color="gray.500" mt="1">
            Keep this secret secure
          </Text>
        </Box>

        {!isAuthenticated && (
          <Button
            colorScheme="blue"
            onClick={handleDropboxBusinessAuth}
            isLoading={loading}
            disabled={!dropBoxAppKey || !dropBoxAppSecret}
            size="lg"
          >
            Authenticate Dropbox Access
          </Button>
        )}

        {showManualInput && (
          <VStack spacing="3" align="stretch">
            <Text fontSize="sm" color="gray.600">
              Paste the authorization code from Dropbox auth:
            </Text>
            <Input
              placeholder="Enter Authorization Code"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
            />
            <Button
              colorScheme="teal"
              onClick={handleManualCodeSubmit}
              isLoading={loading}
              disabled={!manualCode}
            >
              Submit Code
            </Button>
          </VStack>
        )}

        {isAuthenticated && (
          <VStack spacing="4">
            <HStack spacing="3" width="100%">
              <Button
                colorScheme="blue"
                onClick={testAdminConnection}
                isLoading={loading}
                flex="1"
              >
                Get Members List
              </Button>
              <Button
                colorScheme="orange"
                onClick={handleDropboxBusinessAuth}
                variant="outline"
                flex="1"
              >
                Re-authenticate
              </Button>
            </HStack>

            {teamInfo && (
              <Box p="4" bg="gray.50" borderRadius="md" width="100%">
                <Text fontWeight="bold" mb="2">
                  Team Information:
                </Text>
                <VStack align="start" spacing="1" fontSize="sm">
                  <Text>Team Name: {teamInfo.name}</Text>
                  <Text>Team ID: {teamInfo.team_id}</Text>
                  <Text>Licensed Users: {teamInfo.num_licensed_users}</Text>
                  <Text>
                    Provisioned Users: {teamInfo.num_provisioned_users}
                  </Text>
                </VStack>
              </Box>
            )}

            {/* Team Members Section */}
            {teamMembers.length > 0 && (
              <Box width="100%">
                <Divider my="4" />
                <VStack align="start" spacing="3">
                  <HStack justify="space-between" width="100%">
                    <Text fontSize="lg" fontWeight="bold">
                      Team Members ({teamMembers.length})
                    </Text>
                    <HStack spacing="2">
                      {loadingMembers && <Spinner size="sm" />}
                      <Button
                        colorScheme="green"
                        size="sm"
                        onClick={saveTeamMembersToBackend}
                        isLoading={savingMembers}
                        disabled={teamMembers.length === 0}
                      >
                        Save Details
                      </Button>
                    </HStack>
                  </HStack>

                  <Box width="100%" overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Name</Th>
                          <Th>Email</Th>
                          <Th>Status</Th>
                          <Th>Membership</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {teamMembers.map((member) => (
                          <Tr key={member.teamMemberId}>
                            <Td>{member.displayName}</Td>
                            <Td>{member.email}</Td>
                            <Td>
                              <Badge
                                colorScheme={
                                  member.membershipStatus === "active"
                                    ? "green"
                                    : "red"
                                }
                                size="sm"
                              >
                                {member.membershipStatus}
                              </Badge>
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={
                                  member.membershipType === "full"
                                    ? "blue"
                                    : "gray"
                                }
                                size="sm"
                              >
                                {member.membershipType}
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </VStack>
              </Box>
            )}
          </VStack>
        )}

        <Box p="3" bg="blue.50" borderRadius="md">
          <Text fontSize="md" color="blue.700">
            <strong>Note:</strong> This connects to Dropbox Business with
            team-level access. You can view all team members.
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default DropboxBusinessAdminConfig;
